import React, { useMemo, useState } from 'react';
import { Button, Checkbox, Input, Modal, Select, Space, Tag, Typography } from 'antd';
import { TeamOutlined, UsergroupAddOutlined } from '@ant-design/icons';

/**
 * Adds people to a participant / viewer selection in bulk.
 *
 * Two routes, because the two needs are different: the team dropdown answers
 * "add all of ConHome" in two clicks, and the dialog answers "add these nine of
 * twelve" where picking them one at a time is the tedious part.
 *
 * Only ever ADDS. Existing selections are never replaced, so a mis-click cannot
 * wipe a list someone has already curated - removal stays in the Select itself,
 * where it is one click per person and already understood.
 *
 * `users` is the caller's already-scoped candidate list, so team visibility
 * rules are applied once by the caller rather than reimplemented here.
 */
export default function ParticipantBulkAdd({ users = [], value = [], onChange, noun = 'participants' }) {
  const [teamId, setTeamId]   = useState(null);
  const [open, setOpen]       = useState(false);
  const [picked, setPicked]   = useState([]);
  const [search, setSearch]   = useState('');
  const [dlgTeam, setDlgTeam] = useState(null);

  const selected = useMemo(() => new Set(value), [value]);

  // Teams present among the candidates, so the list never offers a team with
  // nobody in it.
  const teams = useMemo(() => {
    const seen = new Map();
    users.forEach((u) => {
      if (u.TeamID != null && u.TeamName && !seen.has(u.TeamID)) seen.set(u.TeamID, u.TeamName);
    });
    return [...seen.entries()]
      .map(([id, name]) => ({ value: id, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [users]);

  const teamNewcomers = useMemo(
    () => (teamId == null ? [] : users.filter((u) => u.TeamID === teamId && !selected.has(u.ID))),
    [users, teamId, selected],
  );

  const add = (ids) => {
    if (!ids.length) return;
    onChange([...new Set([...value, ...ids])]);
  };

  const dialogRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users
      .filter((u) => (dlgTeam == null || u.TeamID === dlgTeam))
      .filter((u) => !q || (u.DisplayName || '').toLowerCase().includes(q))
      .sort((a, b) => (a.DisplayName || '').localeCompare(b.DisplayName || ''));
  }, [users, dlgTeam, search]);

  const selectableIds = dialogRows.filter((u) => !selected.has(u.ID)).map((u) => u.ID);
  const allShownPicked = selectableIds.length > 0 && selectableIds.every((id) => picked.includes(id));

  const openDialog = () => { setPicked([]); setSearch(''); setDlgTeam(teamId); setOpen(true); };

  return (
    <>
      <Space wrap size={8} style={{ marginTop: -8, marginBottom: 8 }}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>Add in bulk:</Typography.Text>
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Whole team…"
          style={{ minWidth: 190 }}
          size="small"
          value={teamId}
          onChange={setTeamId}
          options={teams}
          suffixIcon={<TeamOutlined />}
        />
        <Button
          size="small"
          disabled={!teamNewcomers.length}
          onClick={() => { add(teamNewcomers.map((u) => u.ID)); setTeamId(null); }}
        >
          {teamId == null
            ? 'Add team'
            : teamNewcomers.length
              ? `Add ${teamNewcomers.length}`
              : 'All already added'}
        </Button>
        <Button size="small" icon={<UsergroupAddOutlined />} onClick={openDialog}>
          Choose people…
        </Button>
      </Space>

      <Modal maskClosable={false}
        title={`Add ${noun}`}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => { add(picked); setOpen(false); }}
        okText={picked.length ? `Add ${picked.length}` : 'Add'}
        okButtonProps={{ disabled: !picked.length }}
        width={520}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size={10}>
          <Space wrap size={8} style={{ width: '100%' }}>
            <Select
              allowClear showSearch optionFilterProp="label"
              placeholder="All teams" style={{ minWidth: 180 }}
              value={dlgTeam} onChange={setDlgTeam} options={teams}
            />
            <Input.Search
              allowClear placeholder="Find by name" style={{ width: 200 }}
              value={search} onChange={(e) => setSearch(e.target.value)}
            />
          </Space>

          <Checkbox
            checked={allShownPicked}
            indeterminate={!allShownPicked && picked.length > 0}
            disabled={!selectableIds.length}
            onChange={(e) => setPicked(e.target.checked ? selectableIds : [])}
          >
            Select all shown ({selectableIds.length})
          </Checkbox>

          <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 4, padding: 8 }}>
            {dialogRows.length === 0
              ? <Typography.Text type="secondary">Nobody matches.</Typography.Text>
              : dialogRows.map((u) => {
                const already = selected.has(u.ID);
                return (
                  <div key={u.ID} style={{ padding: '3px 0' }}>
                    <Checkbox
                      disabled={already}
                      checked={already || picked.includes(u.ID)}
                      onChange={(e) => setPicked((p) => (
                        e.target.checked ? [...p, u.ID] : p.filter((x) => x !== u.ID)
                      ))}
                    >
                      {u.DisplayName}
                      {u.TeamName && (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}> · {u.TeamName}</Typography.Text>
                      )}
                      {/* Already-added people stay visible but ticked and locked,
                          so the list reads as the full roster rather than
                          silently hiding who is on it. */}
                      {already && <Tag style={{ marginLeft: 6 }}>added</Tag>}
                    </Checkbox>
                  </div>
                );
              })}
          </div>
        </Space>
      </Modal>
    </>
  );
}
